import type {
  PortariaUser,
  PortariaUserPayload,
} from 'contracts/PortariaUsers';
import type { YardRole, YardRolePayload } from 'contracts/YardRoles';
import { api } from 'services';

const responseItems = <T>(response: T[] | { data: T[] }): T[] =>
  Array.isArray(response) ? response : response.data;

const normalizePortariaUser = (item: any): PortariaUser => ({
  id: item.id,
  name: item.name,
  email: item.email,
  blockedAt: item.blockedAt ?? null,
  loggedAt: item.loggedAt ?? null,
  roles: (item.yardUserRoles ?? [])
    .map((link: any) => link.role)
    .filter(Boolean),
});

const getUsers = async (
  params: Record<string, any> = {}
): Promise<{ data: PortariaUser[]; meta: any }> => {
  const { data } = await api.get('portaria-users', { params });
  return {
    meta: data.meta,
    data: (data.data ?? []).map(normalizePortariaUser),
  };
};

const createUser = async (payload: PortariaUserPayload): Promise<void> => {
  await api.post('auth/register/portaria-member', payload);
};

const updateUser = async (
  id: number,
  payload: PortariaUserPayload
): Promise<void> => {
  await api.put(`portaria-users/${id}`, payload);
};

const deleteUser = async (id: number): Promise<void> => {
  await api.delete(`portaria-users/${id}`);
};

const getRoles = async (
  params: Record<string, any> = {}
): Promise<YardRole[]> => {
  const { data } = await api.get('yard-roles', {
    params: { asList: true, ...params },
  });
  return responseItems<YardRole>(data);
};

const createRole = async (payload: YardRolePayload): Promise<YardRole> => {
  const { data } = await api.post('yard-roles', payload);
  return data;
};

const updateRole = async (
  id: number,
  payload: YardRolePayload
): Promise<YardRole> => {
  const { data } = await api.put(`yard-roles/${id}`, payload);
  return data;
};

const deleteRole = async (id: number): Promise<void> => {
  await api.delete(`yard-roles/${id}`);
};

export function usePortariaAccess() {
  return {
    getUsers,
    createUser,
    updateUser,
    deleteUser,
    getRoles,
    createRole,
    updateRole,
    deleteRole,
  };
}
